// Phase P-LINK-NOTARY-TRIPLE
// Forward edges from /[en/]notary/$act pages into the triple-key translation
// matrix /[en/]translate/$language/$doctype/in/$location.
//
// Why: most notarial acts (POA, Affidavit, Certified Copy, Single-Status...)
// require an accompanying certified English translation of the underlying
// Thai document before they're useful abroad. Surfacing the most-relevant
// triple-key combos on every notary act page closes the journey and pushes
// PageRank from 30 notary hubs into ~5,000 long-tail money pages.

import { Link } from "@tanstack/react-router";
import { ArrowRight, Languages } from "lucide-react";
import { getTranslationDocType } from "@/data/translation-doctypes";

// notary act slug → ordered list of recommended translation doctypes
const ACT_TO_DOCTYPES: Record<string, string[]> = {
  // Signature / acknowledgement
  "certify-signature": ["passport", "id-card", "power-of-attorney", "contract", "company-affidavit"],
  "witness-signature": ["passport", "id-card", "contract", "marriage-certificate"],

  // Affidavit / Jurat / Oath
  jurat: ["affidavit", "passport", "marriage-certificate", "financial-statement", "bank-statement"],
  affidavit: ["affidavit", "passport", "marriage-certificate", "birth-certificate", "house-registration"],
  "statutory-declaration": ["affidavit", "passport", "police-clearance", "marriage-certificate"],
  oath: ["affidavit", "passport", "contract"],

  // Certified copies
  "certified-true-copy": ["passport", "id-card", "house-registration", "diploma", "transcript"],
  "copy-passport": ["passport", "id-card", "house-registration"],
  "copy-education": ["diploma", "transcript", "passport", "id-card"],
  "copy-id": ["id-card", "house-registration", "passport", "marriage-certificate"],
  "copy-license": ["id-card", "passport", "diploma"],

  // Corporate
  "corporate-resolution": ["company-affidavit", "contract", "passport", "financial-statement"],
  "certify-incumbency": ["company-affidavit", "passport", "contract"],
  "certify-good-standing": ["company-affidavit", "financial-statement", "bank-statement"],
  "power-of-attorney": ["power-of-attorney", "passport", "id-card", "house-registration"],
  "specific-poa": ["power-of-attorney", "passport", "id-card"],
  "revocation-poa": ["power-of-attorney", "passport"],

  // Property / inheritance
  "deed-of-gift": ["passport", "id-card", "house-registration", "marriage-certificate"],
  "declaration-of-trust": ["passport", "company-affidavit", "contract"],
  "real-estate-cert": ["passport", "id-card", "house-registration", "contract"],
  "inheritance-affidavit": ["birth-certificate", "marriage-certificate", "house-registration", "passport"],

  // Intl / visa
  "affidavit-single": ["affidavit", "passport", "id-card", "house-registration", "divorce-certificate"],
  "affidavit-support": ["affidavit", "financial-statement", "bank-statement", "passport"],
  "affidavit-name": ["affidavit", "passport", "id-card", "marriage-certificate"],
  "travel-consent": ["birth-certificate", "passport", "id-card", "house-registration"],
  guardianship: ["birth-certificate", "passport", "id-card", "marriage-certificate"],
  "certify-translation": ["diploma", "transcript", "marriage-certificate", "birth-certificate"],
  "certify-photo": ["passport", "id-card"],
  "authentication-fingerprint": ["police-clearance", "passport", "id-card"],
  "certify-no-criminal": ["police-clearance", "passport", "id-card"],
  "apostille-request": ["birth-certificate", "marriage-certificate", "diploma", "transcript", "police-clearance"],

  // Newly added
  "dbd-signature": ["company-affidavit", "passport", "id-card"],
  "life-certificate": ["passport", "id-card", "house-registration"],
  "existence-of-document": ["contract", "company-affidavit", "passport"],
};

const DEFAULT_DOCTYPES = ["passport", "id-card", "marriage-certificate", "birth-certificate", "diploma", "transcript"];

const HUBS = [
  "bangkok-hq",
  "hub-sathorn-cbd",
  "hub-asoke-corridor",
  "hub-phuket-patong",
  "hub-chiangmai-nimman",
  "hub-pattaya",
] as const;

const HUB_LABEL_TH: Record<string, string> = {
  "bangkok-hq": "สำนักงานใหญ่ กรุงเทพฯ",
  "hub-asoke-corridor": "อโศก / สุขุมวิท",
  "hub-sathorn-cbd": "สาทร CBD",
  "hub-phuket-patong": "ภูเก็ต ป่าตอง",
  "hub-chiangmai-nimman": "เชียงใหม่ นิมมาน",
  "hub-pattaya": "พัทยา",
};
const HUB_LABEL_EN: Record<string, string> = {
  "bangkok-hq": "Bangkok HQ",
  "hub-asoke-corridor": "Asoke / Sukhumvit",
  "hub-sathorn-cbd": "Sathorn CBD",
  "hub-phuket-patong": "Phuket / Patong",
  "hub-chiangmai-nimman": "Chiang Mai / Nimman",
  "hub-pattaya": "Pattaya",
};

interface Props {
  mode: "th" | "en";
  actSlug: string;
  actNameTh: string;
  actNameEn: string;
}

export default function NotaryTripleKeyLinks({ mode, actSlug, actNameTh, actNameEn }: Props) {
  const isTh = mode === "th";
  const doctypes = ACT_TO_DOCTYPES[actSlug] ?? DEFAULT_DOCTYPES;

  type Card = { slug: string; d: NonNullable<ReturnType<typeof getTranslationDocType>>; hub: string };
  const cards: Card[] = doctypes
    .map((slug, i): Card | null => {
      const d = getTranslationDocType(slug);
      if (!d) return null;
      return { slug, d, hub: HUBS[i % HUBS.length] as string };
    })
    .filter((x): x is Card => x !== null)
    .slice(0, 6);

  if (cards.length === 0) return null;

  return (
    <section className="mt-14 border-t border-slate-200 pt-10">
      <div className="text-[11px] font-bold uppercase tracking-widest text-violet-700">
        {isTh ? "แปลเอกสารคู่กับงาน Notary" : "Translate the underlying document"}
      </div>
      <h2 className="mt-1 font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
        {isTh
          ? `เอกสารที่มักแปลคู่กับ${actNameTh}`
          : `Documents most often translated alongside ${actNameEn}`}
      </h2>
      <p className="mt-2 max-w-3xl text-sm text-slate-600">
        {isTh
          ? `งาน Notary ใช้ในต่างประเทศต้องมีคำแปลภาษาอังกฤษรับรอง MoJ + ตรา MFA — เลือกเอกสารและฮับที่ใกล้คุณ จบในแพ็คเกจเดียว`
          : `Notarial work used abroad needs a certified English translation + MoJ/MFA legalisation. Pick the document and the hub nearest you — one package, one timeline.`}
      </p>

      <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {cards.map(({ slug, d, hub }) => (
          <Link
            key={`${slug}-${hub}`}
            to={isTh
              ? "/translate/$language/$doctype/in/$location"
              : "/en/translate/$language/$doctype/in/$location"}
            params={{ language: "english", doctype: slug, location: hub }}
            className="group flex items-start gap-3 rounded-2xl border border-slate-200 bg-white p-4 transition hover:-translate-y-0.5 hover:border-violet-400 hover:shadow-md"
            data-track-zone="notary-triple-deep"
          >
            <Languages className="mt-0.5 h-5 w-5 shrink-0 text-violet-600" />
            <div className="flex-1">
              <div className="text-[10px] font-bold uppercase tracking-widest text-violet-700">
                {isTh ? d.nameTh : d.nameEn}
              </div>
              <div className="mt-0.5 font-display text-sm font-extrabold text-navy-deep group-hover:text-violet-700">
                {isTh ? `แปล ${d.nameTh} เป็นภาษาอังกฤษ` : `${d.nameEn} → English`}
              </div>
              <div className="mt-1 text-[11px] text-slate-500">
                {isTh ? `รับ/นัดที่ ${HUB_LABEL_TH[hub] ?? hub}` : `Pick-up at ${HUB_LABEL_EN[hub] ?? hub}`}
              </div>
              <div className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-violet-700 group-hover:gap-1.5">
                {isTh ? "ดูราคา + ขั้นตอน" : "See price + steps"} <ArrowRight className="h-3 w-3" />
              </div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}
